import React from 'react';
import { useAuth } from '../context/AuthContext';
import { getAllRoles, ROLES } from '../utils/roles';
import './EmployeeManagement.css';
import './AdminPanel.css';

const AdminPanel = () => {
  const { currentUser, switchRole } = useAuth();
  
  const roles = [
    { name: 'Super Admin', access: 'Full system access', id: ROLES.SUPER_ADMIN.id },
    { name: 'HR Manager', access: 'Employee & Leave management', id: ROLES.HR_MANAGER.id },
    { name: 'Department Head', access: 'Department-specific access', id: ROLES.DEPARTMENT_HEAD.id },
    { name: 'Team Lead', access: 'Task & Team management', id: ROLES.TEAM_LEAD.id },
    { name: 'Employee', access: 'Basic access', id: ROLES.EMPLOYEE.id }
  ];

  const allRoles = getAllRoles();

  const reports = [
    { icon: 'fa-file-excel', title: 'Attendance Report', desc: 'Monthly attendance summary' },
    { icon: 'fa-file-excel', title: 'Payroll Report', desc: 'Salary disbursement report' },
    { icon: 'fa-file-excel', title: 'Leave Report', desc: 'Leave balance and history' },
    { icon: 'fa-file-excel', title: 'Performance Report', desc: 'Employee performance metrics' }
  ];

  return (
    <div className="admin-panel">
      <div className="module-header">
        <h2><i className="fas fa-shield-alt"></i> Admin Panel</h2>
      </div>

      {/* Role Switcher for Testing */}
      <div className="role-switcher">
        <h3><i className="fas fa-user-cog"></i> Switch Role (Testing)</h3>
        <p>Current Role: <strong>{allRoles.find(r => r.id === currentUser.role)?.name}</strong></p>
        <div className="role-buttons">
          {allRoles.map((role) => (
            <button
              key={role.id}
              className={`role-btn ${currentUser.role === role.id ? 'active' : ''}`}
              onClick={() => switchRole(role.id)}
            >
              <i className="fas fa-user"></i>
              {role.name}
              <div className="role-permissions">
                {allRoles.find(r => r.id === role.id)?.permissions.slice(0, 3).map((perm, i) => (
                  <span key={i} className="permission-badge">{perm}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="admin-sections">
        <div className="admin-card">
          <h3><i className="fas fa-user-shield"></i> Roles & Permissions</h3>
          <div className="role-list">
            {roles.map((role, index) => (
              <div key={index} className="role-item">
                <div>
                  <strong>{role.name}</strong>
                  <p>{role.access}</p>
                </div>
                <button className="btn-small btn-secondary">Manage</button>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <h3><i className="fas fa-chart-bar"></i> Reports</h3>
          <div className="report-list">
            {reports.map((report, index) => (
              <div key={index} className="report-item">
                <i className={`fas ${report.icon}`}></i>
                <div>
                  <strong>{report.title}</strong>
                  <p>{report.desc}</p>
                </div>
                <button className="btn-icon"><i className="fas fa-download"></i></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3><i className="fas fa-cog"></i> System Settings</h3>
        <div className="settings-grid">
          <div className="setting-item">
            <label>Company Name</label>
            <input type="text" className="form-input" defaultValue="RiDeal™ Mobility Drive Pvt Ltd" />
          </div>
          <div className="setting-item">
            <label>Working Hours</label>
            <input type="text" className="form-input" defaultValue="9:00 AM - 6:00 PM" />
          </div>
          <div className="setting-item">
            <label>Default Leave Days</label>
            <input type="number" className="form-input" defaultValue="24" />
          </div>
          <div className="setting-item">
            <label>Currency</label>
            <select className="form-input">
              <option>USD</option>
              <option>EUR</option>
              <option>INR</option>
            </select>
          </div>
        </div>
        <button className="btn btn-primary">Save Settings</button>
      </div>

      <style jsx>{`
        .role-switcher {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 30px;
          border-radius: 16px;
          margin-bottom: 30px;
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
        }

        .role-switcher h3 {
          color: white;
          font-size: 22px;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .role-switcher h3 i {
          font-size: 24px;
        }

        .role-switcher p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 15px;
          margin-bottom: 20px;
        }

        .role-switcher p strong {
          color: white;
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 6px;
          margin-left: 8px;
        }

        .role-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 15px;
        }

        .role-btn {
          background: white;
          border: 3px solid transparent;
          padding: 20px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          gap: 12px;
          text-align: left;
          position: relative;
          overflow: hidden;
        }

        .role-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
          border-color: var(--primary-color);
        }

        .role-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }

        .role-btn i {
          font-size: 28px;
          color: var(--primary-color);
          margin-bottom: 4px;
        }

        .role-btn.active i {
          color: white;
        }

        .role-btn::after {
          content: '';
          position: absolute;
          top: 10px;
          right: 10px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #e5e7eb;
        }

        .role-btn.active::after {
          background: #10b981;
          box-shadow: 0 0 8px #10b981;
        }

        .role-permissions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }

        .permission-badge {
          font-size: 10px;
          padding: 4px 8px;
          background: #f3f4f6;
          color: #374151;
          border-radius: 6px;
          font-weight: 500;
          white-space: nowrap;
        }

        .role-btn.active .permission-badge {
          background: rgba(255, 255, 255, 0.25);
          color: white;
        }

        .admin-sections {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 25px;
          margin-bottom: 30px;
        }

        .admin-card {
          background: white;
          padding: 30px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          border: 1px solid #f3f4f6;
          transition: all 0.3s ease;
        }

        .admin-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }

        .admin-card h3 {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 25px;
          font-size: 22px;
          color: var(--text-primary);
          padding-bottom: 15px;
          border-bottom: 2px solid #f3f4f6;
        }

        .admin-card h3 i {
          color: var(--primary-color);
          font-size: 24px;
        }

        .role-list, .report-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .role-item {
          padding: 18px 20px;
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border-radius: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;
          border: 1px solid #e5e7eb;
        }

        .role-item:hover {
          background: linear-gradient(135deg, #fff 0%, #f9fafb 100%);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateX(4px);
        }

        .role-item strong {
          display: block;
          margin-bottom: 6px;
          font-size: 16px;
          color: var(--text-primary);
        }

        .role-item p {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .report-item {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 18px 20px;
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-radius: 10px;
          transition: all 0.3s ease;
          border: 1px solid #a7f3d0;
        }

        .report-item:hover {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
          transform: translateX(4px);
        }

        .report-item i.fas.fa-file-excel {
          font-size: 32px;
          color: #059669;
        }

        .report-item div {
          flex: 1;
        }

        .report-item strong {
          display: block;
          margin-bottom: 4px;
          font-size: 16px;
          color: #065f46;
        }

        .report-item p {
          font-size: 13px;
          color: #047857;
        }

        .settings-section {
          background: white;
          padding: 30px;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          border: 1px solid #f3f4f6;
        }

        .settings-section h3 {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 25px;
          font-size: 22px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f3f4f6;
        }

        .settings-section h3 i {
          color: var(--primary-color);
          font-size: 24px;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 25px;
          margin-bottom: 25px;
        }

        .setting-item {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .setting-item label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .btn-small {
          padding: 8px 16px;
          font-size: 13px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-small:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 768px) {
          .role-switcher {
            padding: 20px;
          }

          .role-buttons {
            grid-template-columns: 1fr;
          }

          .admin-sections {
            grid-template-columns: 1fr;
          }

          .settings-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;
